import React, { useRef } from 'react';
import {
  LottieContainer,
  LottieContainerWrapper,
  MultiSectionsContent,
  PrivateQuestionAvatar,
  PrivateQuestionAvatarsDiv,
  PrivateQuestionFooter,
  PrivateQuestionLabel,
  PrivateQuestionLabelIcon,
  QuestionBuilderColumnTextPairing,
  QuestionBuilderTopBox,
  QuestionCard,
  QuestionCardContent,
  QuestionCardContentWrapper,
  QuestionCardsContainer,
  QuestionCardShadow,
  QuestionCardTag,
  QuestionCardWrapper,
  QuestionMenuItem,
  QuestionsMenu,
  QuestionSuggestionsColumns,
  SecondSectionBottomTextBlock,
  SecondSectionBox,
  SecondSectionTopTextBlock,
  SectionBox,
  DetailedTextPairing,
  QuestionSuggestionTextPairing,
  SecondSectionTextPairing,
  SecondSectionBottomDetailTextPairing,
  SecondSectionDetailTextPairing,
  PhotoCreditContent,
} from './QuestionBuilderSection.styles';
import HtmlParser from 'react-html-parser';
import { Column, Columns } from '@components/Columns';
import { TextPairing } from '@components/TextPairing';
import { Flex } from '@components/Flex';
import { CloudImage } from '@components/CloudImage';
import { CSSTransition, SwitchTransition } from 'react-transition-group';
import { useQuestionBuilderSectionController } from '@sections/online-rsvp/QuestionBuilderSection/QuestionBuilderSection.controller';
import { LazyLoadWrapper } from '@components/LazyLoadWrapper';
import { Text } from '@components/typography';
import { useTranslation } from 'react-i18next';

type QuestionCardData = Readonly<{
  tagSrc: string;
  tagAlt: string;
  questionCopy: string;
}>;
type QuestionMenuItemData = Readonly<{
  iconSrc: string;
  alt: string;
  card: QuestionCardData;
}>;

interface Props {}

export const QuestionBuilderSection: React.FC<Props> = () => {
  const sectionId = 'Question_Builder';
  const sectionRef = useRef<HTMLDivElement>(null);

  const {
    secondSectionRef,
    multiSectionsContentRef,
    secondSectionTopTextBlockRef,
    secondSectionBottomTextBlockRef,
    questionCardsContainerRef,
    questionCardShadowRef,
    questionLottieRef,
    privateQuestionLabelRef,
    privateQuestionAvatarsDivRef,
    initAssets,
    selectedQuestionIndex,
    setSelectedQuestionIndex,
  } = useQuestionBuilderSectionController();

  const { t } = useTranslation('onlineRsvp');
  const questionBuilderData = t('questionBuilder');
  const questionsData = questionBuilderData.firstSection.questionsMenu.questions;
  const firstSectionData = questionBuilderData.firstSection;
  const secondSectionData = questionBuilderData.secondSection;
  const thirdSectionData = questionBuilderData.thirdSection;

  const questionMenuItems: ReadonlyArray<QuestionMenuItemData> = [
    {
      iconSrc: 'wedding-invitations/question-builder/icon_attendance.svg',
      alt: questionsData[0].alt,
      card: {
        tagSrc: 'wedding-invitations/question-builder/rsvp-tag-attendance.svg',
        tagAlt: questionsData[0].cardTagAlt,
        questionCopy: questionsData[0].cardQuestionCopy,
      },
    },
    {
      iconSrc: 'wedding-invitations/question-builder/icon_address.svg',
      alt: questionsData[1].alt,
      card: {
        tagSrc: 'wedding-invitations/question-builder/rsvp-tag-address.svg',
        tagAlt: questionsData[1].cardTagAlt,
        questionCopy: questionsData[1].cardQuestionCopy,
      },
    },
    {
      iconSrc: 'wedding-invitations/question-builder/icon_phone.svg',
      alt: questionsData[2].alt,
      card: {
        tagSrc: 'wedding-invitations/question-builder/rsvp-tag-phone.svg',
        tagAlt: questionsData[2].cardTagAlt,
        questionCopy: questionsData[2].cardQuestionCopy,
      },
    },
    {
      iconSrc: 'wedding-invitations/question-builder/icon_accommodations.svg',
      alt: questionsData[3].alt,
      card: {
        tagSrc: 'wedding-invitations/question-builder/rsvp-tag-accomodations.svg',
        tagAlt: questionsData[3].cardTagAlt,
        questionCopy: questionsData[3].cardQuestionCopy,
      },
    },
    {
      iconSrc: 'wedding-invitations/question-builder/icon_song-request.svg',
      alt: questionsData[4].alt,
      card: {
        tagSrc: 'wedding-invitations/question-builder/rsvp-tag-song-requests.svg',
        tagAlt: questionsData[4].cardTagAlt,
        questionCopy: questionsData[4].cardQuestionCopy,
      },
    },
    {
      iconSrc: 'wedding-invitations/question-builder/icon_well-wishes.svg',
      alt: questionsData[5].alt,
      card: {
        tagSrc: 'wedding-invitations/question-builder/rsvp-tag-well-wishes.svg',
        tagAlt: questionsData[5].cardTagAlt,
        questionCopy: questionsData[5].cardQuestionCopy,
      },
    },
    {
      iconSrc: 'wedding-invitations/question-builder/icon_dietary-restrictions.svg',
      alt: questionsData[6].alt,
      card: {
        tagSrc: 'wedding-invitations/question-builder/rsvp-tag-dietary.svg',
        tagAlt: questionsData[6].cardTagAlt,
        questionCopy: questionsData[6].cardQuestionCopy,
      },
    },
    {
      iconSrc: 'wedding-invitations/question-builder/icon_destination-wedding.svg',
      alt: questionsData[7].alt,
      card: {
        tagSrc: 'wedding-invitations/question-builder/rsvp-tag-destination.svg',
        tagAlt: questionsData[7].cardTagAlt,
        questionCopy: questionsData[7].cardQuestionCopy,
      },
    },
  ];

  const selectedQuestionCard = questionMenuItems[selectedQuestionIndex].card;

  return (
    <>
      <SectionBox as={'section'} backgroundColor="green5" paddingBottom={13} paddingTop={[10, 0]} ref={sectionRef} id={sectionId}>
        <LazyLoadWrapper beforeLoad={initAssets}>
          <div />
        </LazyLoadWrapper>
        <Columns>
          <Column width="full">
            <QuestionBuilderTopBox>
              <PhotoCreditContent color="yellow3" leftIconVariant offset="top-right" id="photoCreditOnlineRSVPQuestionBuilder" />
              <Columns fluid verticalAlign={'center'}>
                <Column offset={1} width={4}>
                  <TextPairing
                    eyebrow={firstSectionData.eyebrow}
                    title={firstSectionData.title}
                    titleMaxWidth="350px"
                    eyebrowColor="white"
                    titleColor="green1"
                    eyebrowVariant="sectionEyeBrow24"
                    titleVariant="calloutHed40Bold"
                    titleTagName="h2"
                    eyebrowTagName="h2"
                  />
                </Column>
                <Column offset={1} width={6}>
                  <DetailedTextPairing
                    eyebrow="&nbsp;"
                    title={firstSectionData.details}
                    eyebrowVariant="sectionEyeBrow24"
                    titleVariant="dek24"
                    titleColor="green1"
                  />
                </Column>
              </Columns>
              <Columns marginTop={[9, 11]} fluid verticalAlign={'center'}>
                <Column offset={1} width={3}>
                  <QuestionBuilderColumnTextPairing
                    icon="attendance"
                    title={firstSectionData.attendance.title}
                    body={firstSectionData.attendance.body}
                    bodyMaxWidth="230px"
                    titleTagName="h3"
                  />
                </Column>
                <Column offset={1} width={3}>
                  <QuestionBuilderColumnTextPairing
                    icon="multipleChoice"
                    title={firstSectionData.multipleChoice.title}
                    body={firstSectionData.multipleChoice.body}
                    bodyMaxWidth="230px"
                    titleTagName="h3"
                  />
                </Column>
                <Column offset={1} width={3}>
                  <QuestionBuilderColumnTextPairing
                    icon="shortAnswer"
                    title={firstSectionData.shortAnswer.title}
                    body={firstSectionData.shortAnswer.body}
                    bodyMaxWidth="187px"
                    titleTagName="h3"
                  />
                </Column>
              </Columns>
            </QuestionBuilderTopBox>
          </Column>
        </Columns>
        <QuestionSuggestionsColumns>
          <Column width={12}>
            <Flex flexDirection="column" alignItems="center" id="Question_Suggestions">
              <QuestionSuggestionTextPairing
                title={firstSectionData.questionsMenu.title}
                body={HtmlParser(firstSectionData.questionsMenu.body)}
                titleVariant="calloutHed40Bold"
                bodyVariant="dek24"
                titleColor="green1"
                bodyColor="green1"
                bodyMaxWidth="570px"
                alignment="center"
                titleTagName="h3"
              />
              <QuestionsMenu>
                {questionMenuItems.map((menuItem, index) => (
                  <QuestionMenuItem
                    key={index}
                    isActive={selectedQuestionIndex === index}
                    onClick={() => {
                      setSelectedQuestionIndex(index);
                    }}
                  >
                    <CloudImage src={menuItem.iconSrc} alt={menuItem.alt} />
                  </QuestionMenuItem>
                ))}
              </QuestionsMenu>
            </Flex>
          </Column>
        </QuestionSuggestionsColumns>
        <MultiSectionsContent ref={multiSectionsContentRef}>
          <SecondSectionTopTextBlock ref={secondSectionTopTextBlockRef} id="Customizable_Questions">
            <Columns verticalAlign={'flex-end'}>
              <Column offset={1} width={5}>
                <SecondSectionTextPairing
                  eyebrow={secondSectionData.eyebrow}
                  title={secondSectionData.title}
                  eyebrowColor="green5"
                  titleColor="green6"
                  eyebrowVariant="sectionEyeBrow24"
                  titleVariant="calloutHed40Bold"
                  titleTagName="h2"
                  eyebrowTagName="h2"
                />
              </Column>
              <Column width={6}>
                <SecondSectionDetailTextPairing
                  eyebrow=""
                  title={secondSectionData.details}
                  eyebrowVariant="sectionEyeBrow24"
                  titleVariant="dek24"
                  titleColor="green6"
                />
              </Column>
            </Columns>
          </SecondSectionTopTextBlock>
          <QuestionCardsContainer ref={questionCardsContainerRef}>
            <QuestionCardWrapper>
              <QuestionCardShadow ref={questionCardShadowRef} />
              <QuestionCard>
                {/*stub to start loading asset earlier*/}
                <QuestionCardTag src={selectedQuestionCard.tagSrc} alt={selectedQuestionCard.tagAlt} $isHidden />
                <SwitchTransition mode={'out-in'}>
                  <CSSTransition
                    key={selectedQuestionIndex}
                    addEndListener={(node, done) => node.addEventListener('transitionend', done, false)}
                    classNames="fade-in"
                  >
                    <QuestionCardContentWrapper>
                      <QuestionCardTag src={selectedQuestionCard.tagSrc} alt={selectedQuestionCard.tagAlt} />
                      <QuestionCardContent variant="calloutHed48" color="green5" tagName="p">
                        {selectedQuestionCard.questionCopy}
                      </QuestionCardContent>
                    </QuestionCardContentWrapper>
                  </CSSTransition>
                </SwitchTransition>
                <LottieContainerWrapper className={selectedQuestionIndex > 0 ? 'transparent' : ''}>
                  <LottieContainer ref={questionLottieRef} />
                </LottieContainerWrapper>
                <PrivateQuestionFooter>
                  <PrivateQuestionLabel ref={privateQuestionLabelRef}>
                    <PrivateQuestionLabelIcon src="wedding-invitations/question-builder/icon_specific-guests.svg" />
                    <Text variant="featureHed17" color="green5" tagName="p" marginLeft={5}>
                      Ask to 12 Guests
                    </Text>
                  </PrivateQuestionLabel>
                  <PrivateQuestionAvatarsDiv ref={privateQuestionAvatarsDivRef}>
                    <PrivateQuestionAvatar src="common/avatars/avatars_photo_avatar-15.jpg" />
                    <PrivateQuestionAvatar src="common/avatars/avatars_photo_avatar-16.jpg" />
                    <PrivateQuestionAvatar src="common/avatars/avatars_photo_avatar-30.jpg" />
                    <PrivateQuestionAvatar src="common/avatars/avatars_green_plus9.svg" />
                  </PrivateQuestionAvatarsDiv>
                </PrivateQuestionFooter>
              </QuestionCard>
            </QuestionCardWrapper>
          </QuestionCardsContainer>
          <SecondSectionBottomTextBlock ref={secondSectionBottomTextBlockRef} id="Private_Questions">
            <Columns verticalAlign={'flex-end'}>
              <Column offset={1} width={5}>
                <TextPairing
                  eyebrow={thirdSectionData.eyebrow}
                  title={thirdSectionData.title}
                  eyebrowColor="green5"
                  titleColor="green6"
                  eyebrowVariant="sectionEyeBrow24"
                  titleVariant="calloutHed40Bold"
                  titleMaxWidth="329px"
                  titleTagName="h2"
                  eyebrowTagName="h2"
                />
              </Column>
              <Column width={6}>
                <SecondSectionBottomDetailTextPairing
                  eyebrow="&nbsp;"
                  title={thirdSectionData.details}
                  eyebrowVariant="sectionEyeBrow24"
                  titleVariant="dek24"
                  titleColor="green6"
                />
              </Column>
            </Columns>
          </SecondSectionBottomTextBlock>
        </MultiSectionsContent>
      </SectionBox>
      <SecondSectionBox ref={secondSectionRef} as={'section'} backgroundColor="yellow1" paddingTop={11} />
    </>
  );
};
